import type { ReactNode } from 'react'

import Button from '@/component/atoms/Button'
import { Icon } from '@/component/atoms/Icon'
import DualListEmpty from '@/component/molecules/DualList/components/DualListEmpty'
import type {
  StudentLeftEmptyReason,
  StudentRightEmptyReason,
} from '@/component/molecules/DualList/types'
import type { StudentGroupT } from '@/dto'
import commonRepo from '@/repository/common.repo'
import { RouteName } from '@/router'
import modalService from '@/service/modal.service'

const getLeftEmptyReason = (props: {
  isLeftEmpty: boolean
  isRightEmpty: boolean
  isSearchEmpty: boolean
  tabType?: StudentGroupT['type']
  teacherType?: res.Teacher['type']
}): StudentLeftEmptyReason | null => {
  if (props.isSearchEmpty) return 'search-empty'
  if (!props.isLeftEmpty) return null
  if (!props.isRightEmpty) {
    return props.tabType === 'lesson-class' ? 'all-moved-class' : 'all-moved'
  }
  if (props.teacherType === 'HOMEROOM_TEACHER') {
    return props.tabType === 'lesson-class' ? 'homeroom-no-classes' : 'homeroom-no-students'
  }
  return props.tabType === 'lesson-class' ? 'no-classes' : 'no-students'
}

const getRightEmptyReason = (props: {
  isRightEmpty: boolean
  isClassFilterEmpty?: boolean
}): StudentRightEmptyReason | null => {
  if (props.isClassFilterEmpty) return 'class-filter-empty'
  if (props.isRightEmpty) return 'empty'
  return null
}

export const renderGroupedLeftEmptyView = (props: {
  isLeftEmpty: boolean
  isRightEmpty: boolean
  isSearchEmpty: boolean
  isGroupKeysEmpty?: boolean
  tabType?: StudentGroupT['type']
  teacherType?: res.Teacher['type']
}): ReactNode | null => {
  const reason = getLeftEmptyReason(props)
  if (!reason) return null
  return <DualListEmpty>{LEFT_EMPTY_CONTENT[reason]}</DualListEmpty>
}

export const renderGroupedRightEmptyView = (props: {
  isRightEmpty: boolean
  isClassFilterEmpty?: boolean
}): ReactNode | null => {
  const reason = getRightEmptyReason(props)
  if (!reason) return null
  return <DualListEmpty>{RIGHT_EMPTY_CONTENT[reason]}</DualListEmpty>
}

const LEFT_EMPTY_CONTENT: Record<StudentLeftEmptyReason, ReactNode> = {
  'all-moved': '모든 학생을 추가하였습니다.',
  'all-moved-class': (
    <p>
      반에 소속된 모든 학생을
      <br />
      추가하였습니다.
    </p>
  ),
  'search-empty': (
    <p>
      조건에 맞는 결과가 없습니다.
      <br />
      다시 입력해 주세요.
    </p>
  ),
  'no-students': (
    <>
      <p>
        등록된 학생이 없습니다.
        <br />
        먼저 학생을 등록해 주세요.
      </p>
      <Button
        size="m"
        variant="line-filled"
        color="accent"
        leftIcon="person"
        onClick={() => {
          modalService.closeModal()
          commonRepo.router?.push(RouteName.studentManage)
        }}
      >
        학생 등록하러 가기
      </Button>
    </>
  ),
  'no-classes': (
    <>
      <p>
        등록된 반이 없습니다.
        <br />
        먼저 반을 등록해 주세요.
      </p>
      <Button
        size="m"
        variant="line-filled"
        color="accent"
        onClick={() => {
          modalService.closeModal()
          commonRepo.router?.push(RouteName.classManage)
        }}
      >
        반 등록하러 가기
      </Button>
    </>
  ),
  'homeroom-no-students': (
    <p>
      선생님이 담당하고 있는
      <br />
      학생이 없습니다.
    </p>
  ),
  'homeroom-no-classes': (
    <p>
      선생님이 담당하고 있는
      <br />
      반이 없습니다.
    </p>
  ),
}

const RIGHT_EMPTY_CONTENT: Record<StudentRightEmptyReason, ReactNode> = {
  empty: (
    <p>
      <span>
        왼쪽의
        <Icon name="add_circle" size={20} color="var(--Colors-Gray-500)" />를 눌러
      </span>
      <span>학생을 선택해 주세요.</span>
    </p>
  ),
  'class-filter-empty': '반에 소속된 학생만 표시됩니다.',
}
